import {ResponsiveContent} from "../../../ResponsiveNavTabs";
import React, {useRef, useState} from "react";
import {Button, ListGroup, Stack} from "react-bootstrap";
import {Link, useNavigate} from "react-router-dom";
import {useLocalFilter2, useProductOrderOptions, useSelect, useTouch, useLongPress, useProduct, useProductCount, useProductList, useWindowSize} from "../../../../hooks";
import {useAlert, useAuth, useDarkMode} from "../../../../context";
import {
    ItemLoading,
    OurPagination2,
    MenuAdd,
    MenuBar,
    MenuClearSelectButton,
    MenuConfirmDeleteButton,
    MenuOrderBy,
    MenuSearchTerm,
    OurClearAlert,
    PageTitle
} from "../../../_components";
import {config} from "../../../../utils2";
import {ABOverlayTooltip} from "../../../_components/CustomOverlayTooltip";
import {centsToString} from "../utilities/formatCurrency";
import {isoTimestampToReadableDateOnly} from "../utilities/readableDate";

export function Products() {
    return <ResponsiveContent body={<ProductForm/>}/>
}

function ProductForm() {
    const {userId} = useAuth()
    const {success} = useAlert()

    const cache = Date.now()

    // Get order options
    const {orderOptions, errorOrderOptions, clearErrorOrderOptions} = useProductOrderOptions()

    // API Actions
    const {deleteItem, errorValue, clearErrorValue} = useProduct(userId)

    // API count all items
    const {count, setCount, loadingCount, errorCount, clearErrorCount} = useProductCount()

    // API items per page
    const {list, setList, loadingList, errorList, clearErrorList} = useProductList()

    // Filter
    const filteredList = useLocalFilter2(list, ["title"])

    // Select
    const [selList, toggleSelectItems, clearSelectItems, isItemSelected] = useSelect(list)

    function handleOnItemsDelete() {
        selList
            .forEach(item =>
                deleteItem(item.id)
                    .then(() => {
                        setCount(count => count - 1)
                        setList(prev => prev.filter(obj => obj.id !== item.id))
                        success(`${item.name} has been deleted!`)
                    })
                    .catch(err => console.warn(err.message))
            )
        clearSelectItems()
    }


    return <Stack>

        <OurClearAlert variant="danger" msg={errorOrderOptions} onClear={clearErrorOrderOptions}/>
        <OurClearAlert variant="danger" msg={errorCount} onClear={clearErrorCount}/>
        <OurClearAlert variant="danger" msg={errorList} onClear={clearErrorList}/>
        <OurClearAlert variant="danger" msg={errorValue} onClear={clearErrorValue}/>


        <PageTitle title="Products">Manage {list.length} of {count} products.</PageTitle>

        <MenuBar>
            <MenuAdd to="/backend/product/add" icon="plus">Add</MenuAdd>
            <MenuSearchTerm/>
            <MenuOrderBy orderOptions={orderOptions}/>
            <MenuClearSelectButton hide={selList.length === 0} onClick={clearSelectItems}>
                {selList.length} {selList.length === 1 ? "row" : "rows"}
            </MenuClearSelectButton>
            <MenuConfirmDeleteButton hide={selList.length === 0} onSubmit={handleOnItemsDelete}>
                <span>{selList.length} {selList.length === 1 ? "product" : "products"}</span>
            </MenuConfirmDeleteButton>
        </MenuBar>

        <ItemLoading show={loadingCount || loadingList}/>

        {list && list.length > 0 ?
            <ListGroup variant="flush">
                {filteredList.map(item =>
                    <RenderItem key={item.id} item={item} selected={isItemSelected(item.id)} selectedCount={selList.length} onSelect={toggleSelectItems}
                                to={`/backend/product/${item.id}`} cache={cache}/>
                )}
            </ListGroup>
            :
            <span className="text-center">No product added yet</span>
        }

        <OurPagination2 loading={loadingCount || loadingList} itemCount={count}/>

    </Stack>
}

function RenderItem({item, selected, selectedCount, onSelect, to, cache}) {
    const touch = useTouch()
    const {width} = useWindowSize()
    const navigate = useNavigate()
    const [show, setShow] = useState(false)
    const [showFig, setShowFig] = useState(true)

    const ref = useRef(null)
    useLongPress(ref, () => onSelect(item.id))
    const {darkMode} = useDarkMode()

    const statusMap = ["draft", "active", "archived"]
    const statusBorder = item.status === statusMap[0] ? "border-warning" : item.status === statusMap[1] ? "border-success" : "border-danger"

    const size = width < 576 ? "60px" : "80px"


    return (
        // <Stack direction="horizontal" gap="2" className={`mb-1 rounded ${selected && "bg-light"} ${show && "bg-light-subtle"}`}
        <Stack direction="horizontal" gap="2" className={`mb-1 rounded ${(selected || show) && "bg-light-subtle"}`}
               onMouseEnter={() => setShow(true)}
               onMouseLeave={() => setShow(false)}>
            <Stack ref={ref} direction="horizontal" className="flex-grow-1" gap="2">
                <ABOverlayTooltip tip={item.status === statusMap[0] ? statusMap[0] : item.status === statusMap[1] ? statusMap[1] : statusMap[2]} placement="right">
                    <div className={`border border-3 rounded ${statusBorder}`}>
                        <div>
                            <Link to={to}>
                                {showFig ?
                                    <img
                                        src={config.get_public_media(item.medias[0]?.path, "thumb", darkMode)}
                                        alt={item.description}
                                        height={size}
                                        width={size}
                                        style={{borderRadius: "8px", minWidth: "60px"}}
                                        loading="lazy"
                                        className="mb-0"
                                    />
                                    : <div
                                        style={{
                                            width: size,
                                            height: size,
                                            minWidth: "60px",
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '8px',
                                        }}
                                    >
                                        <i className="bi bi-ban text-secondary" style={{fontSize: '40px'}}></i>
                                    </div>
                                    // <div className="mt-0 mb-0 d-flex justify-content-center align-items-center" style={{height: '120px', width: '120px'}}>
                                    //     <i className="bi bi-ban" style={{fontSize: '70px'}}></i>
                                    // </div>
                                }
                            </Link>
                        </div>
                    </div>
                </ABOverlayTooltip>
                <div>
                    <Stack direction="vertical" className="" gap={0}>
                        <Link to={to}
                              className="flex-shrink-0 link-body-emphasis link-underline link-underline-opacity-0 link-underline-opacity-75-hover link-offset-1 link-offset-1-hover">
                            <span className="text-wrap fw-semibold fs-4">{item.title}</span>
                        </Link>
                        <span className="text-wrap fs-6 fw-light">{item.SKU}</span>
                        <span className="text-wrap fs-6 fw-light">€{centsToString(item.prices[0].price)}</span>
                        <div className="text-nowrap fw-normal text-secondary small"><i className="bi bi-plus-lg me-1"/>{isoTimestampToReadableDateOnly(item.created_at)}</div>
                    </Stack>
                </div>
                <div className="ms-auto">
                    <Stack className="text-body-tertiary align-items-end justify-content-center" gap={0}>
                        <span className="text-nowrap fw-normal text-secondary">Stock: {item.stock}</span>
                        <span className="fw-normal text-secondary text-capitalize">{item.status}</span>
                    </Stack>
                </div>
            </Stack>

            <div className="ms-auto ">
                <Stack className="align-items-center justify-content-center">
                    {(!touch || selected || selectedCount > 0) &&
                        <Button className="link-underline link-underline-opacity-0" variant="link" onClick={() => onSelect(item.id)}>
                            <span className={`bi ${selected ? "bi-check-square" : "bi-square"}`}></span>
                        </Button>
                    }
                    <Button variant="link" className="d-none d-md-block" onClick={() => navigate(`/shop/detail/${item.id}`)}><i className="bi bi-bag"/></Button>
                </Stack>
            </div>
        </Stack>
    )
}

